import countriesData from '@/assets/json/countries.json';

// People type country names the way they say them, so the canonical list alone
// cannot resolve an input. Each key here maps a spoken form onto the exact name
// countries.json carries.
const COUNTRY_NAME_VARIATIONS: Record<string, string> = {
    // United States variations
    'united states of america': 'United States',
    'usa': 'United States',
    'america': 'United States',
    'us': 'United States',

    // United Kingdom variations
    'uk': 'United Kingdom',
    'great britain': 'United Kingdom',
    'britain': 'United Kingdom',
    'england': 'United Kingdom',
    'scotland': 'United Kingdom',
    'wales': 'United Kingdom',
    'northern ireland': 'United Kingdom',

    // Other common variations
    'russia': 'Russian Federation',
    'south korea': 'Korea, Republic of',
    'north korea': 'Korea, Democratic People\'s Republic of',
    'venezuela': 'Venezuela, Bolivarian Republic of',
    'bolivia': 'Bolivia, Plurinational State of',
    'iran': 'Iran, Islamic Republic of',
    'syria': 'Syrian Arab Republic',
    'vietnam': 'Viet Nam',
    'macedonia': 'North Macedonia',
    'czech republic': 'Czechia',
    'ivory coast': 'Côte d\'Ivoire',
    'cape verde': 'Cabo Verde',
    'east timor': 'Timor-Leste',
    'swaziland': 'Eswatini',
    'burma': 'Myanmar',
    'holland': 'Netherlands',
    'congo': 'Congo',
};

/**
 * Normalize a country name for comparison
 */
function normalizeCountryName(name: string): string {
    return name.toLowerCase().trim()
        .replace(/[.,]/g, '') // Remove commas and periods
        .replace(/\s+/g, ' '); // Normalize whitespace
}

/**
 * Get country by code (e.g., "US" -> "United States")
 */
function getCountryNameByCode(code: string): string | null {
    const normalizedCode = code.toUpperCase().trim();
    const country = countriesData.find(c => c.code === normalizedCode);
    return country ? country.name : null;
}

/**
 * Get country code by name (e.g., "United States" -> "US")
 */
function getCountryCodeByName(name: string): string | null {
    const normalizedName = normalizeCountryName(name);

    // First check direct match
    const directMatch = countriesData.find(c =>
        normalizeCountryName(c.name) === normalizedName
    );
    if (directMatch) return directMatch.code;

    // Check variations
    const standardName = COUNTRY_NAME_VARIATIONS[normalizedName];
    if (standardName) {
        const country = countriesData.find(c => c.name === standardName);
        return country ? country.code : null;
    }

    // Check partial matches (for cases like "United States of America")
    const partialMatch = countriesData.find(c => {
        const countryName = normalizeCountryName(c.name);
        return countryName.includes(normalizedName) || normalizedName.includes(countryName);
    });

    return partialMatch ? partialMatch.code : null;
}

/**
 * Get country name from either a name or code input
 */
export function getCountryName(input: string): string | null {
    const trimmed = input.trim();

    // If it looks like a code (2 uppercase letters), get name by code
    if (/^[A-Z]{2}$/.test(trimmed)) {
        return getCountryNameByCode(trimmed);
    }

    // If it's already a valid country name, normalize it
    const code = getCountryCodeByName(input);
    return code ? getCountryNameByCode(code) : null;
}
